import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Avatar,
  TextField,
  Alert,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { EventAvailable, Person, CalendarToday, AccessTime, Schedule } from '@mui/icons-material';

/**
 * Student-scoped "Request Class Reschedule" dialog — presentation only.
 *
 * The reason field is controlled by StudentDashboard (`reason` / `onReasonChange`),
 * validation and the disabled state are computed there (`submitDisabled`), and the
 * cancel reset plus the `createRescheduleRequest` submit both live in the parent
 * (`onCancel` / `onSubmit`). This component performs no API calls, no validation,
 * no date math and holds no state.
 */
const DetailRow = ({ icon, label, value }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
    <Box
      sx={{
        width: 34,
        height: 34,
        flex: 'none',
        borderRadius: '10px',
        display: 'grid',
        placeItems: 'center',
        color: 'warning.main',
        bgcolor: (theme) => alpha(theme.palette.warning.main, 0.13),
        '& svg': { fontSize: 18 },
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: '11.5px', color: 'text.secondary' }}>{label}</Typography>
      <Typography sx={{ fontSize: '13.5px', fontWeight: 600, color: 'text.primary' }}>
        {value}
      </Typography>
    </Box>
  </Box>
);

const RescheduleClassDialog = ({
  open,
  onCancel,
  subject,
  teacherName,
  dateLabel,
  timeLabel,
  duration,
  reason,
  onReasonChange,
  loading,
  submitDisabled,
  onSubmit,
}) => (
  <Dialog
    open={open}
    onClose={onCancel}
    maxWidth="sm"
    fullWidth
    PaperProps={{
      sx: {
        borderRadius: '16px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
      },
    }}
  >
    <DialogTitle sx={{ pb: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar sx={{ backgroundColor: 'warning.main' }}>
          <EventAvailable />
        </Avatar>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Request Class Reschedule
        </Typography>
      </Box>
    </DialogTitle>

    <DialogContent sx={{ pt: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        You are requesting to reschedule the following class:
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          p: 2,
          mb: 3,
          borderRadius: '14px',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {subject}
        </Typography>
        <DetailRow icon={<Person />} label="Teacher" value={teacherName} />
        <DetailRow icon={<CalendarToday />} label="Date" value={dateLabel} />
        <DetailRow icon={<AccessTime />} label="Time" value={timeLabel} />
        <DetailRow icon={<Schedule />} label="Duration" value={`${duration} minutes`} />
      </Box>

      <TextField
        fullWidth
        multiline
        rows={4}
        label="Reason for Rescheduling *"
        placeholder="Please explain why you need to reschedule this class (e.g., medical appointment, family emergency, etc.)"
        value={reason}
        onChange={(e) => onReasonChange(e.target.value)}
        sx={{ mb: 2 }}
      />

      <Alert severity="info" sx={{ mt: 2 }}>
        Your request will be sent to the admin. They will contact you and the teacher to arrange a new time.
      </Alert>
    </DialogContent>

    <DialogActions sx={{ p: 3, pt: 0 }}>
      <Button onClick={onCancel} sx={{ borderRadius: 2, textTransform: 'none' }}>
        Cancel
      </Button>
      <Button
        variant="contained"
        onClick={onSubmit}
        disabled={submitDisabled}
        sx={{ borderRadius: 2, textTransform: 'none' }}
      >
        {loading ? 'Submitting...' : 'Submit Request'}
      </Button>
    </DialogActions>
  </Dialog>
);

export default RescheduleClassDialog;
